import React, { useState, useEffect } from 'react';
import './App.css';
import { withAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { get } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

function App({ signOut, user }) {
  const [photos, setPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const [previousTokens, setPreviousTokens] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  async function fetchPhotos(token = null) {
    setIsLoading(true);
    try {
      const apiName = 'AdminAPI';
      let path = '/api/v1/admin/photos?limit=20';
      
      if (token) {
        path += `&nextToken=${encodeURIComponent(token)}`;
      }
    
      // Get the auth session to retrieve the JWT token
      const session = await fetchAuthSession();
      
      try {
        const restOperation = get({
          apiName: apiName,
          path: path,
          options: {
            headers: {
              Authorization: `Bearer ${session.tokens?.idToken?.toString()}`
            }
          }
        });

        const response = await restOperation.response;
        
        // Parse the ReadableStream body as JSON
        const photosData = await response.body.json();

        if (photosData && photosData.photos) {
          setPhotos(photosData.photos);
          setNextToken(photosData.nextToken || null);
        } else {
          console.error('Unexpected data format:', photosData);
          throw new Error('Unexpected data format in API response');
        }
      } catch (error) {
        console.error('Error fetching photos:', error);
        throw error;
      }
    } catch (error) {
      console.error('error fetching photos', error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchPhotos();
  }, []);

  const getPresignedUrls = async (photoId) => {
    try {
      const apiName = 'AdminAPI';
      const path = `/api/v1/admin/photos/${photoId}/presigned-url`;
      
      // Get the auth session to retrieve the JWT token
      const session = await fetchAuthSession();
      
      const restOperation = get({
        apiName: apiName,
        path: path,
        options: {
          headers: {
            Authorization: `Bearer ${session.tokens?.idToken?.toString()}`
          }
        }
      });
      const response = await restOperation.response;
      
      // Parse the ReadableStream body as JSON
      const presignedUrlData = await response.body.json();
      
      if (presignedUrlData && presignedUrlData.mediumUrl && presignedUrlData.fullUrl) {
        return {
          mediumUrl: presignedUrlData.mediumUrl,
          fullUrl: presignedUrlData.fullUrl
        };
      } else {
        console.error('Unexpected presigned URL format:', presignedUrlData);
        throw new Error('Unexpected data format in presigned URL response');
      }
    } catch (error) {
      console.error('Error fetching presigned urls:', error);
      return null;
    }
  };

  const openModal = async (photo) => {
    setSelectedPhoto(photo);
    const urls = await getPresignedUrls(photo.photoId);
    if (urls) {
      setSelectedPhotoUrl(urls.mediumUrl);
      setModalOpen(true);
    }
  };

  const downloadPhoto = async (photo, event) => {
    event.stopPropagation(); // Prevent opening modal when clicking download
    try {
      const urls = await getPresignedUrls(photo.photoId);
      if (urls && urls.fullUrl) {
        // Open the presigned URL which now includes Content-Disposition: attachment
        // This will trigger a download instead of opening in a new tab
        window.open(urls.fullUrl, '_blank');
      }
    } catch (error) {
      console.error('Error downloading photo:', error);
    }
  };

  const closeModal = () => {
    setSelectedPhoto(null);
    setSelectedPhotoUrl('');
    setModalOpen(false);
  };

  const handleNextPage = () => {
    if (nextToken) {
      setPreviousTokens([...previousTokens, nextToken]);
      fetchPhotos(nextToken);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePreviousPage = () => {
    if (previousTokens.length > 0) {
      const newPreviousTokens = [...previousTokens];
      newPreviousTokens.pop();
      setPreviousTokens(newPreviousTokens);
      
      const previousToken = newPreviousTokens[newPreviousTokens.length - 1] || null;
      fetchPhotos(previousToken);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFirstPage = () => {
    setPreviousTokens([]);
    fetchPhotos(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="App">
      <header>
        <h1>Photo Gallery</h1>
        <button onClick={signOut}>Sign Out</button>
      </header>
      <div className="gallery">
        {photos.map((photo) => (
          <div key={photo.photoId} className="thumbnail" onClick={() => openModal(photo)}>
            <div className="thumbnail-image-container">
              <img src={photo.thumbnailUrl} alt={`thumbnail-${photo.photoId}`} />
            </div>
            <div className="thumbnail-info">
              <p className="uploader-name">Uploader: {photo.uploaderName}</p>
              <button
                className="download-btn"
                onClick={(e) => downloadPhoto(photo, e)}
                title="Download photo"
              >
                ⬇ Download
              </button>
            </div>
          </div>
        ))}
      </div>

      {isLoading && (
        <div className="loading-indicator">
          <p>Loading photos...</p>
        </div>
      )}

      {!isLoading && photos.length > 0 && (
        <div className="pagination">
          <button
            onClick={handleFirstPage}
            disabled={previousTokens.length === 0}
            className="pagination-btn"
          >
            ⏮ First
          </button>
          <button
            onClick={handlePreviousPage}
            disabled={previousTokens.length === 0}
            className="pagination-btn"
          >
            ← Previous
          </button>
          <span className="pagination-info">
            Page {previousTokens.length + 1}
          </span>
          <button
            onClick={handleNextPage}
            disabled={!nextToken}
            className="pagination-btn"
          >
            Next →
          </button>
        </div>
      )}

      {modalOpen && selectedPhoto && (
        <div className="modal">
          <div className="modal-content">
            <span className="close" onClick={closeModal}>&times;</span>
            {selectedPhotoUrl ? (
              <img src={selectedPhotoUrl} alt={`full-${selectedPhoto.photoId}`} />
            ) : (
              <p>Loading...</p>
            )}
            <p>Uploader: {selectedPhoto.uploaderName}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default withAuthenticator(App);
